import { useState, useRef, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { LuArrowLeft, LuSend, LuBot, LuUser, LuMessageSquare } from "react-icons/lu";
import { API } from "../App";

const ChatTestPage = ({ auth }) => {
  const { agentId } = useParams();
  const [agent, setAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchAgent();
  }, [agentId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchAgent = async () => {
    try {
      const response = await fetch(`${API}/agents/${agentId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAgent(data);
        // Add welcome message
        if (data.welcome_message) {
          setMessages([{
            role: "assistant",
            content: data.welcome_message,
            timestamp: new Date().toISOString()
          }]);
        }
      }
    } catch (error) {
      toast.error("Error al cargar agente");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMessage = {
      role: "user",
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setSending(true);

    try {
      const response = await fetch(`${API}/chat`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          agent_id: agentId,
          message: userMessage.content,
          phone_number: "test_user"
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, {
          role: "assistant",
          content: data.response,
          timestamp: new Date().toISOString()
        }]);
      } else {
        toast.error("Error al obtener respuesta");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" className="rounded-xl" data-testid="back-btn">
              <LuArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <LuBot className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-foreground">{agent?.name}</h1>
              <p className="text-xs text-muted-foreground">Modo de prueba</p>
            </div>
          </div>
        </div>
      </header>

      {/* Chat Area */}
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-4">
        <Card className="glass-card border-white/10 h-[calc(100vh-200px)] flex flex-col">
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <LuMessageSquare className="w-8 h-8 text-primary" />
                  </div>
                  <p className="text-muted-foreground">
                    Escribe un mensaje para probar tu agente
                  </p>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div className={`flex items-end gap-2 max-w-[85%] ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        msg.role === "user" ? "bg-primary/10" : "bg-secondary/20"
                      }`}>
                        {msg.role === "user" ? (
                          <LuUser className="w-4 h-4 text-primary" />
                        ) : (
                          <LuBot className="w-4 h-4 text-secondary-foreground" />
                        )}
                      </div>
                      <div className={`rounded-2xl px-4 py-3 ${
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground rounded-br-md"
                          : "bg-muted text-foreground rounded-bl-md"
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
              {sending && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="flex items-end gap-2">
                    <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center">
                      <LuBot className="w-4 h-4 text-secondary-foreground" />
                    </div>
                    <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                        <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                        <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-4 border-t border-border">
            <form onSubmit={handleSend} className="flex gap-3">
              <Input
                placeholder="Escribe un mensaje..."
                className="flex-1 bg-muted/50 border-transparent focus:border-primary h-12 rounded-xl"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={sending}
                data-testid="chat-input"
              />
              <Button
                type="submit"
                className="rounded-xl bg-primary hover:bg-primary/90 h-12 px-6"
                disabled={!input.trim() || sending}
                data-testid="chat-send-btn"
              >
                <LuSend className="w-5 h-5" />
              </Button>
            </form>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default ChatTestPage;
